export function resolveGradebookRosterRaw(classId, currentClass, classUsers, usersByClass) {
  if (!classId) {
    return [];
  }
  if (currentClass?.id === classId && Array.isArray(classUsers) && classUsers.length > 0) {
    return classUsers;
  }
  const mapped = usersByClass?.[classId];
  return Array.isArray(mapped) ? mapped : [];
}

export function filterGradebookStudentsByActiveStatus(classUsers, activeInactive) {
  const mode = activeInactive === 'inactive' ? 'inactive' : 'active';
  if (!Array.isArray(classUsers)) {
    return [];
  }
  return classUsers.filter((u) => {
    if (u.role !== 'student') {
      return false;
    }
    const classActive = typeof u?.userclass?.active === 'boolean' ? u.userclass.active : true;
    const accountActive = u?.active !== false;
    if (mode === 'inactive') {
      return !classActive || !accountActive;
    }
    return classActive && accountActive;
  });
}

export function getFilteredRosterForGradebookDownload(
  classId,
  activeInactive,
  selectedStudents,
  currentClass,
  classUsers,
  usersByClass
) {
  const raw = resolveGradebookRosterRaw(classId, currentClass, classUsers, usersByClass);
  const roster = filterGradebookStudentsByActiveStatus(raw, activeInactive);
  if (selectedStudents.length > 0) {
    const sel = new Set(selectedStudents.map((k) => String(k.id)));
    return roster.filter((u) => sel.has(String(u.id)));
  }
  return roster;
}

export function getGradebookDownloadStudentIds(
  classId,
  activeInactive,
  selectedStudents,
  currentClass,
  classUsers,
  usersByClass
) {
  return getFilteredRosterForGradebookDownload(
    classId,
    activeInactive,
    selectedStudents,
    currentClass,
    classUsers,
    usersByClass
  ).map((k) => k.id);
}

export function studentMatchesGradebookActiveFilter(user, activeInactive, strictAccountActive = true) {
  if (!user || user.role !== 'student') {
    return false;
  }
  const classActive = typeof user?.userclass?.active === 'boolean' ? user.userclass.active : true;
  const accountActive = user?.active !== false;
  if (activeInactive === 'inactive') {
    return !classActive || !accountActive;
  }else if(activeInactive === 'active') {
     return classActive && accountActive;
  }
  if (strictAccountActive === false) {
    return classActive;
  }
  return classActive && accountActive;
}

export function mergeExamUserWithClassRoster(raw, classUsers) {
  if (!raw || !Array.isArray(classUsers)) {
    return raw;
  }
  const id = raw.id || raw.userId;
  if (!id) {
    return raw;
  }
  const full = classUsers.find((c) => c.id === id || c.ucId === id);
  if (!full) {
    return raw;
  }
  return { ...full, ...raw };
}

function getGradebookResultRowUserId(row) {
  if (!row || Array.isArray(row)) {
    return null;
  }
  const nested = row.user && typeof row.user === 'object' ? row.user : null;
  const id = row.id ?? row.userId ?? row.user_id ?? nested?.id ?? nested?.userId;
  return id == null ? null : String(id);
}

function normalizeGradebookUsername(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim().toLowerCase();
}

function normalizeGradebookDisplayName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function addRosterDisplayNameKeys(displayNames, u) {
  const ln = (u?.lastName || '').trim();
  const fn = (u?.firstName || '').trim();
  if (!ln && !fn) {
    return;
  }
  const variants = [
    `${ln}, ${fn}`,
    `${ln} ,${fn}`,
    `${fn} ${ln}`,
    `${ln} ${fn}`
  ];
  variants.forEach((v) => {
    const k = normalizeGradebookDisplayName(v);
    if (k) {
      displayNames.add(k);
    }
  });
}

function buildAllowedKeysFromRoster(rosterStudents) {
  const ids = new Set();
  const usernames = new Set();
  const displayNames = new Set();
  if (!Array.isArray(rosterStudents)) {
    return { ids, usernames, displayNames };
  }
  rosterStudents.forEach((u) => {
    if (u?.id != null) {
      ids.add(String(u.id));
    }
    if (u?.ucId != null) {
      ids.add(String(u.ucId));
    }
    const un = normalizeGradebookUsername(u?.username);
    if (un) {
      usernames.add(un);
    }
    addRosterDisplayNameKeys(displayNames, u);
  });
  return { ids, usernames, displayNames };
}

function gradebookResultRowMatchesAllowed(row, allowedIds, allowedUsernames, allowedDisplayNames) {
  if (row == null) {
    return false;
  }
  if (Array.isArray(row)) {
    const un = normalizeGradebookUsername(row[1]);
    if (un !== '' && allowedUsernames.has(un)) {
      return true;
    }
    const dn = normalizeGradebookDisplayName(row[0]);
    return dn !== '' && allowedDisplayNames.has(dn);
  }
  const uid = getGradebookResultRowUserId(row);
  if (uid != null && allowedIds.has(uid)) {
    return true;
  }
  const rowUsername = normalizeGradebookUsername(row.username ?? row.user?.username);
  if (rowUsername !== '' && allowedUsernames.has(rowUsername)) {
    return true;
  }
  const nameField = row.name ?? row.user?.name;
  if (nameField) {
    const dn = normalizeGradebookDisplayName(nameField);
    if (dn !== '' && allowedDisplayNames.has(dn)) {
      return true;
    }
  }
  const fn = row.firstName ?? row.user?.firstName;
  const ln = row.lastName ?? row.user?.lastName;
  if (fn || ln) {
    const dn = normalizeGradebookDisplayName(`${ln || ''}, ${fn || ''}`);
    if (dn !== '' && allowedDisplayNames.has(dn)) {
      return true;
    }
  }
  return false;
}

export function filterGradebookApiResultRows(result, rosterOrAllowedIds, options = {}) {
  const { treatEmptyAllowedAsNoRows = false } = options;
  if (!Array.isArray(result)) {
    return result;
  }

  let rosterStudents = null;
  let allowedIds = null;
  let allowedUsernames = null;
  let allowedDisplayNames = null;

  if (Array.isArray(rosterOrAllowedIds)) {
    rosterStudents = rosterOrAllowedIds;
  } else if (rosterOrAllowedIds instanceof Set) {
    allowedIds = new Set([...rosterOrAllowedIds].map((x) => String(x)));
    allowedUsernames = new Set();
    allowedDisplayNames = new Set();
  } else {
    return result;
  }

  if (rosterStudents) {
    if (rosterStudents.length === 0) {
      return treatEmptyAllowedAsNoRows ? [] : result;
    }
    const keys = buildAllowedKeysFromRoster(rosterStudents);
    allowedIds = keys.ids;
    allowedUsernames = keys.usernames;
    allowedDisplayNames = keys.displayNames;
  }

  if (!allowedIds || allowedIds.size === 0) {
    if (!allowedUsernames || allowedUsernames.size === 0) {
      if (!allowedDisplayNames || allowedDisplayNames.size === 0) {
        return treatEmptyAllowedAsNoRows ? [] : result;
      }
    }
  }

  return result.filter((row) => gradebookResultRowMatchesAllowed(
    row,
    allowedIds,
    allowedUsernames,
    allowedDisplayNames
  ));
}
